import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import WithPermission from "components/WithPermission";
import NestedSidenav, { getActiveKeyPath } from "./NestedSidenav";

// Soft UI Dashboard React context
import { useSoftUIController, setMiniSidenav } from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const [openMenus, setOpenMenus] = useState(false);
  const [openMultiLevelKeyPath, setOpenMultiLevelKeyPath] = useState([]);

  useEffect(() => {
    let newPath = null;
    for (let r of routes) {
      const p = getActiveKeyPath(r, pathname);
      if (p) {
        newPath = p;
        break;
      }
    }
    setOpenMultiLevelKeyPath(newPath || []);
  }, [pathname, routes]);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)

  function isRouteActive(node, collapseName) {
    if (node.key === collapseName) return true;
    if (!node.children) return false;
    return node.children.some((child) => isRouteActive(child, collapseName), collapseName);
  }
  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      noCollapse,
      key,
      href,
      route,
      sidebar_item,
      lang_attr,
      permission,
      children,
      user_permission,
    }) => {
      let returnValue;

      if (sidebar_item) {
        if (type === "collapse") {
          const isActive = isRouteActive({ key, children }, collapseName);
          if (children) {
            returnValue = (
              <div key={key}>
                <WithPermission
                  key={key}
                  {...(user_permission ? { userPermission: user_permission } : { permission })}
                >
                  <SidenavCollapse
                    color={color}
                    name={t(`lang.${lang_attr}`)}
                    icon={icon}
                    active={isActive}
                    noCollapse={noCollapse}
                    open={openMultiLevelKeyPath[0] === key}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenMultiLevelKeyPath((prev) => (prev[0] === key ? [] : [key]));
                    }}
                  >
                    {children.map((child) => (
                      <WithPermission
                        key={child.key}
                        {...(child.user_permission
                          ? { userPermission: child.user_permission }
                          : { permission: child.permission })}
                      >
                        <NestedSidenav
                          key={child.key}
                          item={child}
                          color={color}
                          level={1}
                          openMultiLevelKeyPath={openMultiLevelKeyPath}
                          setOpenMultiLevelKeyPath={setOpenMultiLevelKeyPath}
                        />
                      </WithPermission>
                    ))}
                  </SidenavCollapse>
                </WithPermission>
              </div>
            );
          } else {
            returnValue = href ? (
              <WithPermission
                key={key}
                {...(user_permission ? { userPermission: user_permission } : { permission })}
              >
                <Link
                  href={href}
                  key={key}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ textDecoration: "none" }}
                >
                  <SidenavCollapse
                    color={color}
                    name={t(`lang.${lang_attr}`)}
                    icon={icon}
                    active={key === collapseName}
                    noCollapse={noCollapse}
                    onClick={(e) => {
                      setOpenMultiLevelKeyPath([]);
                    }}
                  />
                </Link>
              </WithPermission>
            ) : (
              <WithPermission
                key={key}
                {...(user_permission ? { userPermission: user_permission } : { permission })}
              >
                <NavLink to={route} key={key}>
                  <SidenavCollapse
                    color={color}
                    key={key}
                    name={t(`lang.${lang_attr}`)}
                    icon={icon}
                    active={key === collapseName}
                    noCollapse={noCollapse}
                    onClick={(e) => {
                      setOpenMultiLevelKeyPath([]);
                    }}
                  />
                </NavLink>
              </WithPermission>
            );
          }
        } else if (type === "title") {
          returnValue = (
            <SoftTypography
              key={key}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              opacity={0.6}
              pl={3}
              mt={2}
              mb={1}
              ml={1}
            >
              {title}
            </SoftTypography>
          );
        } else if (type === "divider") {
          returnValue = <Divider key={key} />;
        }
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <SoftBox component="img" src={brand} alt="Soft UI Logo" width="2rem" />}
          <SoftBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <SoftTypography component="h6" variant="button" fontWeight="medium">
              {brandName}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Divider />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
